// D37: the project's test command, editable where the "Edit the test command" decision lands
// (`/projects/{id}/settings?field=test_command` focuses it). Saved through the project PATCH with the workspaces; the
// command execution policy is never sent (the API keeps the stored one). Maintainer, like every project setting.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import type { ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { saveTestCommands, type WorkspaceRow } from "../../api/endpoints-blocks.ts";
import { isApiError } from "../../api/client.ts";
import { Button } from "../../components/Button.tsx";
import { Banner, localErrorMessage } from "../../components/Feedback.tsx";
import { TextField } from "../../components/Field.tsx";
import { useToast } from "../../components/Toast.tsx";
import { useProjectRole } from "../wave3/common.tsx";

export function TestCommandCard({ project, workspaces, onSaved }: { project: ProjectSummary; workspaces: readonly WorkspaceRow[] | undefined; onSaved: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const canEdit = useProjectRole(project.id).atLeast("maintainer");
  const initial = (workspaces ?? []).map((w) => (typeof w.commands["test"] === "string" ? (w.commands["test"] as string) : ""));
  const [values, setValues] = useState<string[]>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const [params] = useSearchParams();
  const focus = params.get("field") === "test_command";
  const key = JSON.stringify(initial);
  useEffect(() => setValues(JSON.parse(key) as string[]), [key]);
  useEffect(() => {
    if (!focus || !firstRef.current) return;
    firstRef.current.scrollIntoView({ block: "center" });
    firstRef.current.focus();
  }, [focus, workspaces]);
  const dirty = JSON.stringify(values) !== key;
  const save = async () => {
    if (!workspaces) return;
    setBusy(true);
    setError(null);
    try {
      await saveTestCommands(project.id, project.version, workspaces, values);
      toast(t("testCommand.saved"), "success");
      onSaved();
    } catch (e) {
      setError(e);
      if (isApiError(e) && e.code === "version_conflict") onSaved();
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card card--pad stack" aria-labelledby="test-command-title" data-testid="test-command-card">
      <h2 id="test-command-title" className="card-title">
        {t("testCommand.title")}
      </h2>
      <p className="field-hint">{t("testCommand.hint")}</p>
      {workspaces && workspaces.length === 0 ? <p className="subtle">{t("testCommand.none")}</p> : null}
      {(workspaces ?? []).map((w, i) => (
        <TextField
          key={`${w.runner_id}:${w.root_path}:${w.subpath}`}
          ref={i === 0 ? firstRef : undefined}
          label={t("testCommand.workspace", { path: `${w.root_path}${w.subpath ? `/${w.subpath}` : ""}` })}
          value={values[i] ?? ""}
          onChange={(e) => setValues((v) => v.map((x, k) => (k === i ? e.target.value : x)))}
          spellCheck={false}
          className="mono"
          maxLength={2000}
          disabled={!canEdit || busy}
          data-testid={`test-command-${i}`}
        />
      ))}
      {error ? (
        <Banner tone="error" role="alert">
          {localErrorMessage(error, t)}
        </Banner>
      ) : null}
      {workspaces && workspaces.length > 0 ? (
        <div className="row">
          <Button variant="primary" size="sm" loading={busy} disabled={!dirty || !canEdit} onClick={() => void save()} data-testid="test-command-save">
            {t("common.save")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
